import React, { useMemo } from 'react';
import { ThemeIcon, Text, Badge, Alert, Group } from '@mantine/core';
import {
	formatAt,
	formatKg,
	movementIcon,
	movementTypeLabel,
	roleColor,
	roleIcon,
	roleLabel,
	statusChangeBulletVariant,
	statusChangeColor,
	statusChangeIcon,
	statusChangeLabel,
} from './traceUi';
import { buildNarrativeGuideTimeline } from './traceTimelineOrder';

const BULLET_SIZE = 36;
const ANCHOR_BULLET_SIZE = 44;
const ICON_FONT = 16;
const GAP = 16;
const LINE_COLOR = '#dee2e6';

function ResidueSummary({ items }) {
	if (!Array.isArray(items) || items.length === 0) {
		return (
			<Text size='xs' color='dimmed'>
				Sin resumen de residuos.
			</Text>
		);
	}
	return (
		<ul className='mb-0 ps-3' style={{ listStyleType: 'disc' }}>
			{items.slice(0, 5).map((r, i) => (
				<li key={`${r.name}-${i}`} style={{ fontSize: 12 }}>
					<span className='fw-semibold'>{r.name}</span>
					{r.category ? (
						<span className='text-muted'> · {r.category}</span>
					) : null}
					{r.kg != null ? (
						<span className='text-muted'> · {formatKg(r.kg)}</span>
					) : null}
					{r.lines && r.lines > 1 ? (
						<span className='text-muted'> · {r.lines} líneas</span>
					) : null}
				</li>
			))}
			{items.length > 5 && (
				<li className='text-muted' style={{ fontSize: 12 }}>
					+{items.length - 5} residuo(s) más
				</li>
			)}
		</ul>
	);
}

/**
 * Renders one row of the timeline. Custom flex layout so we fully control
 * spacing and avoid Mantine Timeline CSS quirks.
 */
function EventRow({
	bulletColor,
	bulletIcon,
	bulletVariant = 'light',
	bulletSize = BULLET_SIZE,
	lineStyle = 'solid',
	isLast = false,
	title,
	badges,
	children,
}) {
	return (
		<div style={{ display: 'flex', gap: GAP, position: 'relative' }}>
			<div
				style={{
					flex: `0 0 ${ANCHOR_BULLET_SIZE}px`,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}
			>
				<ThemeIcon
					size={bulletSize}
					radius='xl'
					color={bulletColor}
					variant={bulletVariant}
				>
					<i
						className={bulletIcon}
						style={{
							fontSize: bulletSize >= 40 ? 18 : ICON_FONT,
						}}
					/>
				</ThemeIcon>
				{!isLast && (
					<div
						style={{
							flex: 1,
							width: 0,
							borderLeft: `2px ${lineStyle} ${LINE_COLOR}`,
							marginTop: 4,
							minHeight: 24,
						}}
					/>
				)}
			</div>
			<div style={{ flex: 1, paddingBottom: isLast ? 0 : 20, minWidth: 0 }}>
				<Group spacing={6} style={{ rowGap: 4, flexWrap: 'wrap' }}>
					<span
						className='fw-semibold'
						style={{ fontSize: 14, lineHeight: '20px' }}
					>
						{title}
					</span>
					{badges}
				</Group>
				<div className='mt-2'>{children}</div>
			</div>
		</div>
	);
}

function GuideStepRow({ step, isLast }) {
	const role = step.role || 'intermediate';
	const color = roleColor(role);
	const icon = roleIcon(role);
	const folio = step.folio || step.guide_number;
	const primary = folio != null ? `Folio ${folio}` : 'Sin folio registrado';
	const isAnchor = !!step.anchor;

	return (
		<EventRow
			bulletColor={color}
			bulletIcon={icon}
			bulletVariant={isAnchor ? 'filled' : 'light'}
			bulletSize={isAnchor ? ANCHOR_BULLET_SIZE : BULLET_SIZE}
			lineStyle='solid'
			isLast={isLast}
			title={primary}
			badges={
				<>
					<Badge size='sm' color={color} variant='light' radius='sm'>
						{roleLabel(role)}
					</Badge>
					{isAnchor && (
						<Badge size='sm' color='indigo' variant='outline' radius='sm'>
							Consultada
						</Badge>
					)}
				</>
			}
		>
			{step.establishment_name && (
				<Text size='xs' color='dimmed'>
					Establecimiento: {step.establishment_name}
				</Text>
			)}
			{step.final_destination_name && (
				<Text size='xs' color='dimmed'>
					Destino final: {step.final_destination_name}
				</Text>
			)}
			{step.status_final_destination_name && (
				<Text size='xs' color='dimmed'>
					Estado DF: {step.status_final_destination_name}
				</Text>
			)}
			<div className='mt-1'>
				<ResidueSummary items={step.residue_summary} />
			</div>
			<Text size='xs' color='dimmed' mt={6}>
				{formatAt(step.at)}
			</Text>
		</EventRow>
	);
}

function MovementRow({ event, isLast }) {
	const delta = Number(event.delta_kg ?? 0);
	const isIntakeEvent =
		event.movement_type === 'INTAKE_DIRECT' ||
		event.movement_type === 'INTAKE_CANCELLATION' ||
		!!event.intake;
	// Color distintivo (violeta) para eventos de intake manual (D-019, F5).
	const color = isIntakeEvent
		? event.movement_type === 'INTAKE_CANCELLATION'
			? 'pink'
			: 'grape'
		: delta > 0
			? 'green'
			: delta < 0
				? 'red'
				: 'gray';
	const icon = movementIcon(event.movement_type);

	return (
		<EventRow
			bulletColor={color}
			bulletIcon={icon}
			bulletVariant={isIntakeEvent ? 'filled' : 'light'}
			lineStyle={isIntakeEvent ? 'solid' : 'dotted'}
			isLast={isLast}
			title={movementTypeLabel(event.movement_type)}
			badges={
				<>
					<Badge size='sm' color={color} variant='light' radius='sm'>
						{event.delta_kg != null
							? `${delta > 0 ? '+' : ''}${formatKg(delta)}`
							: 'Δ —'}
					</Badge>
					{isIntakeEvent && (
						<Badge size='sm' color={color} variant='outline' radius='sm'>
							Ingreso manual
						</Badge>
					)}
				</>
			}
		>
			{event.intake ? (
				<>
					<Text size='xs' color='dimmed'>
						Ingreso manual: {event.intake.residue_name || 'Residuo'} ·{' '}
						{formatKg(event.intake.kilograms)}
					</Text>
					<Text size='xs' color='dimmed' mt={2}>
						Responsable:{' '}
						{event.intake.actor_name || (
							<span className='text-muted fst-italic'>no registrado</span>
						)}
					</Text>
					{event.intake.origin_description ? (
						<Text size='xs' color='dimmed' mt={2}>
							Observación: {event.intake.origin_description}
						</Text>
					) : null}
				</>
			) : (
				<Text size='xs' color='dimmed'>
					{event.guide_folio
						? `Folio ${event.guide_folio}`
						: event.guide_id
							? 'Guía sin folio'
							: 'Sin guía asociada'}
					{event.counterpart_guide_folio ? (
						<>
							<span className='mx-1'>→</span>
							{`Folio ${event.counterpart_guide_folio}`}
						</>
					) : event.counterpart_guide_id ? (
						<>
							<span className='mx-1'>→</span>
							Guía relacionada sin folio
						</>
					) : null}
				</Text>
			)}
			<Text size='xs' color='dimmed' mt={4}>
				{event.at ? formatAt(event.at) : 'Fecha no registrada'}
			</Text>
		</EventRow>
	);
}

function StatusChangeRow({ event, isLast }) {
	const color = statusChangeColor(event);
	const icon = statusChangeIcon(event);
	const title = statusChangeLabel(event);

	return (
		<EventRow
			bulletColor={color}
			bulletIcon={icon}
			bulletVariant={statusChangeBulletVariant()}
			lineStyle='dotted'
			isLast={isLast}
			title={title}
			badges={
				<Badge size='sm' color={color} variant='filled' radius='sm'>
					Guía
				</Badge>
			}
		>
			<Text size='xs' color='dimmed'>
				{event.guide_folio
					? `Folio ${event.guide_folio}`
					: event.guide_id
						? 'Guía sin folio'
						: 'Guía no identificada'}
			</Text>
			<Text size='xs' color='dimmed' mt={2}>
				<span className='text-muted'>Responsable: </span>
				{event.actor_name ? (
					<span className='text-body'>{event.actor_name}</span>
				) : (
					<span className='text-muted fst-italic'>
						no registrado en bitácora
					</span>
				)}
			</Text>
			<Text size='xs' color='dimmed' mt={4}>
				{event.at ? formatAt(event.at) : 'Fecha no registrada'}
			</Text>
		</EventRow>
	);
}

/**
 * @param {{ trace: object|null, entityId: string|null }} props
 */
function TimelineSectionHeader({ title, subtitle, isFirst }) {
	return (
		<div
			className='mb-3 mt-1'
			style={{
				borderTop: isFirst ? undefined : '1px solid #e9ecef',
				paddingTop: isFirst ? 0 : 12,
			}}
		>
			<Text size='sm' weight={600}>
				{title}
			</Text>
			{subtitle ? (
				<Text size='xs' color='dimmed' mt={2}>
					{subtitle}
				</Text>
			) : null}
		</div>
	);
}

const TraceabilityTimeline = ({ trace, entityId = null }) => {
	const { sections } = useMemo(
		() => buildNarrativeGuideTimeline(trace, entityId),
		[trace, entityId],
	);

	const eventCount = useMemo(
		() => sections.reduce((n, s) => n + s.events.length, 0),
		[sections],
	);

	if (!trace) return null;

	if (trace.error) {
		const msg =
			trace.error === 'guide_not_found'
				? 'Guía no encontrada.'
				: trace.error === 'invalid_guide_id'
					? 'No se pudo obtener la trazabilidad de esta guía.'
					: 'No se pudo obtener la trazabilidad.';
		return (
			<Alert color='yellow' mt='sm'>
				{msg}
			</Alert>
		);
	}

	if (!eventCount) {
		return (
			<Alert color='blue' variant='light' className='mb-0'>
				Esta guía aún no tiene eventos en la línea de tiempo.
			</Alert>
		);
	}

	return (
		<>
			{/* Info cabecera vs cadena — desactivado por ahora
			<Alert color='blue' variant='light' className='mb-3'>
				<Text size='xs' color='dimmed'>
					Los cambios de estado de cabecera detallados corresponden solo a la guía
					consultada; el resto de la cadena se resume en cada tarjeta de paso.
				</Text>
			</Alert>
			*/}
			{trace.truncated && (
				<Alert
					color='yellow'
					variant='light'
					className='mb-3'
					title='Trazabilidad acotada'
				>
					Se muestran los primeros eventos por claridad. Hay más registros
					disponibles en el historial.
				</Alert>
			)}
			<div style={{ paddingLeft: 4, paddingRight: 4 }}>
				{sections.map((section, sectionIdx) => (
						<div key={section.id}>
							<TimelineSectionHeader
								title={section.title}
								subtitle={section.subtitle}
								isFirst={sectionIdx === 0}
							/>
							{section.events.map((ev, idx) => {
								const isLast =
									sectionIdx === sections.length - 1 &&
									idx === section.events.length - 1;
								if (ev.kind === 'guide_step') {
									return (
										<GuideStepRow
											key={`step-${ev.guide_id}-${section.id}-${idx}`}
											step={ev}
											isLast={isLast}
										/>
									);
								}
								if (ev.kind === 'status_change') {
									return (
										<StatusChangeRow
											key={`st-${ev.guide_id}-${ev.guide_detail_id || ''}-${section.id}-${idx}`}
											event={ev}
											isLast={isLast}
										/>
									);
								}
								return (
									<MovementRow
										key={`mv-${section.id}-${idx}`}
										event={ev}
										isLast={isLast}
									/>
								);
							})}
						</div>
				))}
			</div>
		</>
	);
};

export default TraceabilityTimeline;
